import React, { FC, useState, FormEvent, ChangeEvent, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ErrorDisplay from './components/ErrorDisplay';
import api from './api';
import './App.css';
import QuillHtmlEditor from './components/QuillHtmlEditor';

interface Organization {
  id: string;
  name: string;
}

const CreateEventPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const quillRef = useRef<any>(null);
  const [quillRange, setQuillRange] = useState<any>(null);
  const [quillLastChange, setQuillLastChange] = useState<any>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [formState, setFormState] = useState({
    name: '',
    startsAt: '',
    endsAt: '',
    place: '',
    summary: '',
    description: '', // This will store the Delta content as JSON string
    imageAlt: '',
    coordinates: '{"lat": 40.7128, "lng": -74.0060}', // Default as JSON string
    image: null as File | null,
    organizationId: '',
  });

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const params = new URLSearchParams();
        params.append('include', 'id');
        params.append('include', 'name');
        params.append('include', 'summary');
        params.append('includeHidden', 'true');

        const data = await api(`/organizations?${params.toString()}`);
        setOrganizations(data.organizations);
        if (data.organizations.length > 0) {
          setFormState(prevState => ({ ...prevState, organizationId: data.organizations[0].id }));
        }
      } catch (err) {
        console.error('Failed to fetch organizations', err);
      }
    };
    fetchOrganizations();
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prevState => ({ ...prevState, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFormState(prevState => ({ ...prevState, image: files[0] }));
    }
  };

  // Handle Quill editor text changes with debouncing
  const handleQuillTextChange = useCallback((delta: any, oldContents: any, source: any) => {
    // Only update if the change is from user input, not from programmatic changes
    if (source === 'user') {
      setQuillLastChange(delta);
      
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Debounce the form state update to prevent cursor issues
      updateTimeoutRef.current = setTimeout(() => {
        if (quillRef.current) {
          const currentDelta = quillRef.current.getContents();
          
          setFormState(prevState => ({
            ...prevState,
            description: JSON.stringify(currentDelta) // Store Delta as JSON string
          }));
        }
      }, 100); // 100ms debounce
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formState.image) {
      setError('Image is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    
    // Convert datetime-local values to Unix timestamps
    const startsAtTimestamp = Math.floor(new Date(formState.startsAt).getTime() / 1000);
    const endsAtTimestamp = Math.floor(new Date(formState.endsAt).getTime() / 1000);

    // Add all form fields to FormData
    Object.entries(formState).forEach(([key, value]) => {
      if (value !== null) {
        if (key === 'startsAt') {
          formData.append(key, startsAtTimestamp.toString());
        } else if (key === 'endsAt') {
          formData.append(key, endsAtTimestamp.toString());
        } else {
          formData.append(key, value);
        }
      }
    });

    try {
      await api('/events', {
        method: 'POST',
        body: formData,
      });
      navigate('/events');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Create New Event</h1>
      <form onSubmit={handleSubmit} className="event-creation-form">
        {error && (
          <ErrorDisplay 
            error={error}
            title="Failed to create event"
            showReferenceId={true}
          />
        )}
        
        <div className="form-group">
          <label htmlFor="name">Event Name</label>
          <input type="text" id="name" name="name" value={formState.name} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="time">Start Time</label>
          <input type="datetime-local" id="startsAt" name="startsAt" value={formState.startsAt} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="endsAt">Ends At</label>
          <input type="datetime-local" id="endsAt" name="endsAt" value={formState.endsAt} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="place">Place</label>
          <input type="text" id="place" name="place" value={formState.place} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="summary">Summary</label>
          <textarea id="summary" name="summary" value={formState.summary} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <QuillHtmlEditor
            ref={quillRef}
            defaultValue={formState.description}
            onSelectionChange={setQuillRange}
            onTextChange={handleQuillTextChange}
            placeholder="Enter a detailed description of your event..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="organizationId">Organization</label>
          <select id="organizationId" name="organizationId" value={formState.organizationId} onChange={handleInputChange} required>
            <option value="" disabled>Select an organization</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="image">Event Image</label>
          <input type="file" id="image" name="image" accept="image/*" onChange={handleFileChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="imageAlt">Image Alt Text (Optional)</label>
          <input 
            type="text" 
            id="imageAlt" 
            name="imageAlt" 
            value={formState.imageAlt} 
            onChange={handleInputChange}
            placeholder="Describe the image for accessibility (e.g., 'People gathering at community event')"
          />
        </div>

        {/* Hidden or advanced fields for now */}
        {/* <input type="hidden" name="hostingOrganizations" value={formState.hostingOrganizations} /> */}
        {/* <input type="hidden" name="coordinates" value={formState.coordinates} /> */}

        <button type="submit" className="button-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Create Event'}
        </button>
      </form>
    </div>
  );
};

export default CreateEventPage; 